import { useMutation } from '@tanstack/react-query';
import processSystemFile from '../../../../utils/processSystemFile';
import urls from '../../../../utils/urls';
import { APITypes } from '../../../../primary-record-types';
import useNotify from '../../../../hooks/useNotify';
import { getIntl } from '../../../../utils/intl';
import { PickedImage } from '..';
import { appAPI } from '../../../../utils/api';
import { notifyBugsnag } from '../../../../utils/bugsnag';

const intl = getIntl('useTempImage', {
    uploadError: 'Error uploading file',
});

export type TempImageValue = {
    base64: string;
    tempFileId: string;
    name: string;
    contentType: string;
};

export type UseTempImageReturnValue = {
    onFileSelected: (pickedImage: PickedImage) => void;
    isUploading: boolean;
};

export type UseTempImageArgs = {
    value: TempImageValue | null;
    onChange: (value: TempImageValue | null) => void;
};

export default function useTempImage({ onChange }: UseTempImageArgs): UseTempImageReturnValue {
    const notify = useNotify();
    const uploadImageMutation = useMutation({
        onMutate: async (pickedImage: PickedImage) => {
            const { uri, name, contentType, s3UploadBuffer: buffer } = await processSystemFile({ pickedImage });

            const response = (await appAPI.post(urls.api.files.tempFiles, {
                body: {
                    name,
                    contentType,
                } as APITypes.fileService.CreateTempFileBody,
            })) as APITypes.fileService.CreateTempFileResponse;

            try {
                const { tempFileId, uploadUrl } = response;

                const uploadResponse = await fetch(uploadUrl, {
                    method: 'PUT',
                    body: buffer as BodyInit,
                    headers: {
                        'Content-Type': contentType,
                        'Content-Encoding': 'base64',
                    },
                });

                if (uploadResponse.ok) {
                    onChange({ base64: uri, tempFileId, name, contentType });
                } else {
                    notify('error', intl('uploadError'));
                    console.error('Error uploading file:', uploadResponse.status, uploadResponse);
                }
            } catch (e) {
                console.error('Error uploading file:', e);
                notify('error', intl('uploadError'));
                notifyBugsnag(e as any);
            }
        },
    });

    return {
        onFileSelected: uploadImageMutation.mutate,
        isUploading: uploadImageMutation.isPending,
    };
}
